<template>
  <div class="hedis-report" :class="{'unified-report': isUnifiedReport}">
    <div class="div-selection">
      <div class="section-box">
        <h4>{{ $t('hedisReport.imgQualityAdequate') }}</h4>
        <div class="eye-quality-container">
          <div class="form-floating v-select-floating">
            <v-select
              class="v-select form"
              id="od_quality_select"
              v-model="imageQuality.OD"
              :options="imageQualityOptions"
              :reduce="x => x.value"
              :clearable="false"
              :searchable="false"
              @option:selected="handleImageQualityChange('OD')">
            </v-select>
            <label for="od_quality_select">{{ $t('hedisReport.rightEye') }}</label>
          </div>
          <div class="form-floating v-select-floating">
            <v-select
              class="v-select form"
              id="os_quality_select"
              v-model="imageQuality.OS"
              :options="imageQualityOptions"
              :reduce="x => x.value"
              :clearable="false"
              :searchable="false"
              @option:selected="handleImageQualityChange('OS')">
            </v-select>
            <label for="os_quality_select">{{ $t('hedisReport.leftEye') }}</label>
          </div>
        </div>
      </div>

      <div class="section-box">
        <h4>{{ $t('hedisReport.diabetesType') }}</h4>
        <div class="form-floating v-select-floating" id="select-type">
          <v-select
            class="v-select form"
            id="select-diabetes-type"
            v-model="selectedDiabetesType"
            :options="diabetesTypeOptions"
            :reduce="x => x.value"
            :clearable="true"
            :searchable="false"
            :placeholder="$t('hedisReport.selectType')"
            @option:selected="handleDiabetesTypeChange">
          </v-select>
          <label for="select-diabetes-type">{{ $t('hedisReport.type') }}</label>
        </div>
      </div>
    </div>

    <div v-if="selectedDiabetesType" class="diabetes-conditions-section">
      <h4>{{ $t('hedisReport.condition') }}</h4>
      <div class="conditions-group">
        <div class="eye-conditions-group" v-if="isRightEyeQualityAdequate">
          <div class="form-floating v-select-floating">
            <v-select
              class="v-select form"
              :id="`od_condition_select`"
              v-model="eyeConditions.OD"
              :options="currentRightEyeConditions" :reduce="x => x.value"
              :clearable="true"
              :searchable="false"
              :placeholder="$t('hedisReport.selectCondition')"
              @option:selected="emitFullUpdate">
              <template v-slot:option="option">
                <span>{{ option.value }} - {{ option.label }}</span>
              </template>
            </v-select>
            <label :for="`od_condition_select`">{{ $t('hedisReport.rightEye') }}</label>
          </div>
        </div>

        <div class="eye-conditions-group" v-if="isLeftEyeQualityAdequate">
          <div class="form-floating v-select-floating">
            <v-select
              class="v-select form"
              :id="`os_condition_select`"
              v-model="eyeConditions.OS"
              :options="currentLeftEyeConditions" :reduce="x => x.value"
              :clearable="true"
              :searchable="false"
              :placeholder="$t('hedisReport.selectCondition')"
              @option:selected="emitFullUpdate">
              <template v-slot:option="option">
                <span>{{ option.value }} - {{ option.label }}</span>
              </template>
            </v-select>
            <label :for="`os_condition_select`">{{ $t('hedisReport.leftEye') }}</label>
          </div>
        </div>
      </div>
    </div>

    <div class="section-box">
      <h4>{{ $t('hedisReport.ExamCPT') }}</h4>

      <div class="cpt-common-intro">
        <!-- <i class="fa fa-info-circle"></i> -->
        <IconInfo/>
        <p>{{ $t('hedisReport.cpt_common_intro') }}</p>
      </div>

      <div class="cpt-container" v-if="!isUnifiedReport">
        <div v-for="cpt in cptOptions" :key="cpt.value" class="cpt-item">
          <div class="checkbox-item">
            <input
              type="radio" :id="`cpt_${cpt.value}`"
              :value="cpt.value"
              v-model="selectedCptCode" name="cpt_category_selection" @change="emitFullUpdate">
            <label :for="`cpt_${cpt.value}`">
              <span class="dx-code">{{ cpt.value }}</span> - {{ $t(cpt.shortLabelKey) }}
            </label>
          </div>
          <!-- <div v-if="selectedCptCode === cpt.value" class="cpt-full-description">
              {{ $t(cpt.fullDescriptionKey) }}
          </div> -->
        </div>
      </div>

      <div class="cpt-container" v-else>
        <div v-for="cpt in cptOptions" :key="cpt.value" class="cpt-item">
          <div class="checkbox-item">
            <BaseRadioButton
              :id="`cpt_${cpt.value}`"
              :item="cpt.value"
              :checked="selectedCptCode === cpt.value"
              @checked="selectedCptCode = $event"/>
            <label :for="`cpt_${cpt.value}`">
              <span class="dx-code">{{ cpt.value }}</span> - {{ $t(cpt.shortLabelKey) }}
            </label>
          </div>
          </div>
      </div>
    </div>

    <div class="section-box">
      <h4>{{ $t('hedisReport.providerComments') }}</h4>
      <div class="provider-comments" v-if="!isUnifiedReport">
        <div class="checkbox-item">
          <input
            type="checkbox"
            id="comment_no_referral"
            :value="$t('hedisReport.comment_no_referral')"
            v-model="selectedProviderComments"
            @change="emitFullUpdate">
          <label for="comment_no_referral">
            {{ $t('hedisReport.comment_no_referral') }}
          </label>
        </div>

        <div class="checkbox-item">
          <input
            type="checkbox"
            id="comment_attest"
            :value="$t('hedisReport.comment_attest')"
            v-model="selectedProviderComments"
            @change="emitFullUpdate">
          <label for="comment_attest">
            {{ $t('hedisReport.comment_attest') }}
          </label>
        </div>
      </div>

      <div class="provider-comments" v-else>
        <div class="checkbox-item">
          <div class="form-check">
            <BaseSquareCheckBox
              id="comment_no_referral"
              :item="$t('hedisReport.comment_no_referral')"
              :initial-checked="selectedProviderComments.includes($t('hedisReport.comment_no_referral'))"
              @checked="handleArrayUpdate($event, $t('hedisReport.comment_no_referral'), selectedProviderComments)"/>
            <label class="form-check-label" for="comment_no_referral">
              {{ $t('hedisReport.comment_no_referral') }}
            </label>
          </div>
        </div>

        <div class="checkbox-item">
          <div class="form-check">
            <BaseSquareCheckBox
              id="comment_attest"
              :item="$t('hedisReport.comment_attest')"
              :initial-checked="selectedProviderComments.includes($t('hedisReport.comment_attest'))"
              @checked="handleArrayUpdate($event, $t('hedisReport.comment_attest'), selectedProviderComments)"/>
            <label class="form-check-label" for="comment_attest">
              {{ $t('hedisReport.comment_attest') }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <div class="section-box">
      <h4>{{ $t('hedisReport.referralFollowUpStatus') }}</h4>
      <div class="form-floating v-select-floating">
        <v-select
          class="v-select form"
          id="select-referral-status"
          v-model="selectedReferralStatus"
          :options="referralOptions"
          :reduce="x => x.value"
          :clearable="false"
          :searchable="false"
          @option:selected="emitFullUpdate">
        </v-select>
        <label for="select-referral-status">{{ $t('hedisReport.referral') }}</label>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.hedis-report {
  display:                       flex;
  flex-direction:                column;
  gap:                           1rem;
  width:                         100%;
  margin-top:                    -1rem;

  h4 {
    color:                       var(--Shades-90);
    font-size:                   var(--font-size-14);
    font-weight:                 bold;
  }

  .div-selection {
    display:                     flex;
    align-items:                 center;
    gap:                         1.5rem;
    width:                       100%;

    > div {
      flex:                      4 1 1.5rem;
    }

    > div:last-child {
      flex:                      1 1 0;
    }

    #select-type {
      display:                   flex;
      flex-direction:            column;
      width:                     100%;
    }
  }

  .eye-quality-container {
    display:                     flex;
    gap:                         1.5rem;
    width:                       100%;

    > .form-floating {
      flex:                      1;
      min-width:                 0;
    }

    .v-select {
      :deep(.vs__dropdown-menu) {
        width:                   100%;
        min-width:               100%;
      }

      :deep(.vs__dropdown-option) {
        white-space:             normal;
        line-height:             1.3;
      }
    }
  }

  .form-floating {
    label {
      color:                     var(--Shades-60);
      font-size:                 var(--font-size-14);
      font-weight:               bold;
    }

    .v-select {
      :deep(.vs__selected) {
        font-size:               var(--font-size-14);
        text-overflow:           ellipsis;
        white-space:             nowrap;
        overflow:                hidden;
        margin-top:              1px;
      }

      :deep(.vs__search::placeholder),
      :deep(.vs__search) {
        font-size:               var(--font-size-14);
        margin-left:             4px;
      }

      :deep(.vs__dropdown-option) {
        font-size:               var(--font-size-14);
      }
    }
  }

  .diabetes-conditions-section {
    display:                     flex;
    flex-direction:              column;
    width:                       100%;

    .conditions-group {
      display:                   flex;
      flex-direction:            column;
      gap:                       1.5rem;
    }

    .eye-conditions-group {
      gap:                       12px;
      width:                     100%;

      label {
        color:                   var(--Shades-80);
        font-size:               var(--font-size-14);
        font-weight:             normal;
        cursor:                  pointer;
      }
    }
  }

  .cpt-container {
    display:                     flex;
    flex-wrap:                   wrap;
    gap:                         1.5rem;
  }

  .cpt-item {
    display:                     flex;
    flex-direction:              column;
    flex:                        1 1 calc(50% - 0.75rem);
  }

  .cpt-full-description {
    color:                       var(--Shades-70);
    font-size:                   var(--font-size-12);
    margin-top:                  0.25rem;
    padding-left:                1.5rem;
  }

  .cpt-common-intro {
    display:                     flex;
    align-items:                 center;
    gap:                         0.5rem;
    margin-bottom:               1rem;

    svg {
      flex-shrink:               0;
    }

    p {
      color:                     var(--Shades-80);
      font-size:                 var(--font-size-14);
      margin-bottom:             0;
    }
  }

  .checkbox-item {
    display:                     flex;
    align-items:                 center;

    input[type="checkbox"] {
      margin-right:              0.5rem;
    }

    input[type="radio"] {
      margin-right:              0.5rem;
    }

    label {
      color:                     var(--Shades-80);
      font-size:                 var(--font-size-14);
      font-weight:               normal;
      cursor:                    pointer;
    }

    :deep(.base-square-checkbox.checked:after) {
      border-color:              var(--Brand-Secondary, #3CB391);
    }
  }

  .provider-comments {
    display:                     flex;
    flex-wrap:                   wrap;
    gap:                         1.5rem;
  }

  .provider-comments .checkbox-item {
    flex:                        1 1 calc(50% - 0.75rem);

    label {
      word-wrap:                 break-word;
      white-space:               normal;
      display:                   block;
    }

    input[type="checkbox"] {
      margin-right:              0.5rem;
    }
  }

  &.unified-report {
    .div-selection {
      padding-top:               1rem;

      h4 {
        display:                 none;
      }

      .section-box {
        flex:                    4 1 1.5rem;

        &:last-child {
          flex:                  1 1 0;
        }
      }

      > div {
        width:                   100%;
      }
    }

    p,
    .checkbox-item label {
      color:                     var(--Brand-Primary);
      font-size:                 var(--font-size-12);
    }

    .form-check {
      margin-bottom:             0;
      background:                transparent !important;
    }

    .checkbox-item .form-check {
      @include                   border_radius(0);
      padding:                   0;
      border:                    0;

      label {
        font-weight:             400;
        letter-spacing:          unset;
        line-height:             normal;
        text-transform:          initial;
      }
    }
  }
}

@media (max-width: 991px) {
  .hedis-report {
    &.unified-report {
      .div-selection,
      .cpt-container,
      .provider-comments {
        flex-wrap:               wrap;

        .cpt-item,
        .checkbox-item {
          flex:                  unset;
        }
      }
    }
  }
}
</style>

<script>
export default {
  name: 'ReportHedis',
  props: {
    initialData: {
      type: Object,
      default: () => ({
        imageQuality: { OD: 'Yes', OS: 'Yes' },
        selectedDiabetesType: '',
        eyeConditions: { OD: '', OS: '' },
        selectedCptCodes: [],
        selectedProviderComments: [],
        selectedReferralStatus: '6 Months'
      })
    },
    isUnifiedReport: {
      type: Boolean,
      required: false,
    }
  },
  data() {
    return {
      imageQuality: { ...this.initialData.imageQuality },
      selectedDiabetesType: this.initialData.selectedDiabetesType,
      eyeConditions: { ...this.initialData.eyeConditions },
      selectedCptCode: this.initialData.selectedCptCodes?.[0] || '',
      selectedProviderComments: [...this.initialData.selectedProviderComments],
      selectedReferralStatus: this.initialData.selectedReferralStatus,

      diabetesType1ConditionsRightEye: [
        { value: 'E10.9', label: this.$t('hedisReport.diabetesType1Conditions.E10_9') },
        { value: 'E10.3211', label: this.$t('hedisReport.diabetesType1Conditions.E10_3211') },
        { value: 'E10.3291', label: this.$t('hedisReport.diabetesType1Conditions.E10_3291') },
        { value: 'E10.3311', label: this.$t('hedisReport.diabetesType1Conditions.E10_3311') },
        { value: 'E10.3391', label: this.$t('hedisReport.diabetesType1Conditions.E10_3391') },
        { value: 'E10.3411', label: this.$t('hedisReport.diabetesType1Conditions.E10_3411') },
        { value: 'E10.3491', label: this.$t('hedisReport.diabetesType1Conditions.E10_3491') },
        { value: 'E10.3511', label: this.$t('hedisReport.diabetesType1Conditions.E10_3511') },
        { value: 'E10.3591', label: this.$t('hedisReport.diabetesType1Conditions.E10_3591') },
      ],
      diabetesType2ConditionsRightEye: [
        { value: 'E11.9', label: this.$t('hedisReport.diabetesType2Conditions.E11_9') },
        { value: 'E11.3211', label: this.$t('hedisReport.diabetesType2Conditions.E11_3211') },
        { value: 'E11.3291', label: this.$t('hedisReport.diabetesType2Conditions.E11_3291') },
        { value: 'E11.3311', label: this.$t('hedisReport.diabetesType2Conditions.E11_3311') },
        { value: 'E11.3391', label: this.$t('hedisReport.diabetesType2Conditions.E11_3391') },
        { value: 'E11.3411', label: this.$t('hedisReport.diabetesType2Conditions.E11_3411') },
        { value: 'E11.3491', label: this.$t('hedisReport.diabetesType2Conditions.E11_3491') },
        { value: 'E11.3511', label: this.$t('hedisReport.diabetesType2Conditions.E11_3511') },
        { value: 'E11.3591', label: this.$t('hedisReport.diabetesType2Conditions.E11_3591') },
      ],
      diabetesType1ConditionsLeftEye: [
        { value: 'E10.9', label: this.$t('hedisReport.diabetesType1Conditions.E10_9') },
        { value: 'E10.3212', label: this.$t('hedisReport.diabetesType1Conditions.E10_3212') },
        { value: 'E10.3292', label: this.$t('hedisReport.diabetesType1Conditions.E10_3292') },
        { value: 'E10.3312', label: this.$t('hedisReport.diabetesType1Conditions.E10_3312') },
        { value: 'E10.3392', label: this.$t('hedisReport.diabetesType1Conditions.E10_3392') },
        { value: 'E10.3412', label: this.$t('hedisReport.diabetesType1Conditions.E10_3412') },
        { value: 'E10.3492', label: this.$t('hedisReport.diabetesType1Conditions.E10_3492') },
        { value: 'E10.3512', label: this.$t('hedisReport.diabetesType1Conditions.E10_3512') },
        { value: 'E10.3592', label: this.$t('hedisReport.diabetesType1Conditions.E10_3592') },
      ],
      diabetesType2ConditionsLeftEye: [
        { value: 'E11.9', label: this.$t('hedisReport.diabetesType2Conditions.E11_9') },
        { value: 'E11.3212', label: this.$t('hedisReport.diabetesType2Conditions.E11_3212') },
        { value: 'E11.3292', label: this.$t('hedisReport.diabetesType2Conditions.E11_3292') },
        { value: 'E11.3312', label: this.$t('hedisReport.diabetesType2Conditions.E11_3312') },
        { value: 'E11.3392', label: this.$t('hedisReport.diabetesType2Conditions.E11_3392') },
        { value: 'E11.3412', label: this.$t('hedisReport.diabetesType2Conditions.E11_3412') },
        { value: 'E11.3492', label: this.$t('hedisReport.diabetesType2Conditions.E11_3492') },
        { value: 'E11.3512', label: this.$t('hedisReport.diabetesType2Conditions.E11_3512') },
        { value: 'E11.3592', label: this.$t('hedisReport.diabetesType2Conditions.E11_3592') },
      ],
      diabetesUnspecifiedConditions: [
        { value: 'E13.3211', label: this.$t('hedisReport.diabetesUnspecifiedConditions.E13_3211') },
        { value: 'E13.3212', label: this.$t('hedisReport.diabetesUnspecifiedConditions.E13_3212') },
        { value: 'E13.3291', label: this.$t('hedisReport.diabetesUnspecifiedConditions.E13_3291') },
        { value: 'E13.3292', label: this.$t('hedisReport.diabetesUnspecifiedConditions.E13_3292') },
        { value: 'E13.3311', label: this.$t('hedisReport.diabetesUnspecifiedConditions.E13_3311') },
        { value: 'E13.3312', label: this.$t('hedisReport.diabetesUnspecifiedConditions.E13_3312') },
        { value: 'E13.3391', label: this.$t('hedisReport.diabetesUnspecifiedConditions.E13_3391') },
        { value: 'E13.3392', label: this.$t('hedisReport.diabetesUnspecifiedConditions.E13_3392') },
        { value: 'E13.3411', label: this.$t('hedisReport.diabetesUnspecifiedConditions.E13_3411') },
        { value: 'E13.3412', label: this.$t('hedisReport.diabetesUnspecifiedConditions.E13_3412') },
        { value: 'E13.3491', label: this.$t('hedisReport.diabetesUnspecifiedConditions.E13_3491') },
        { value: 'E13.3492', label: this.$t('hedisReport.diabetesUnspecifiedConditions.E13_3492') },
        { value: 'E13.3511', label: this.$t('hedisReport.diabetesUnspecifiedConditions.E13_3511') },
        { value: 'E13.3512', label: this.$t('hedisReport.diabetesUnspecifiedConditions.E13_3512') },
      ],

      imageQualityOptions: [
        { value: 'Yes', label: this.$t('hedisReport.yes') },
        { value: 'uninterpretable', label: this.$t('hedisReport.imageQualityOptions.uninterpretable') },
        { value: 'uninterpretable_cataract', label: this.$t('hedisReport.imageQualityOptions.uninterpretable_cataract') },
        { value: 'uninterpretable_corneal_opacity', label: this.$t('hedisReport.imageQualityOptions.uninterpretable_corneal_opacity') },
        { value: 'uninterpretable_pupillary_miosis', label: this.$t('hedisReport.imageQualityOptions.uninterpretable_pupillary_miosis') },
        { value: 'uninterpretable_vitreous_opacity', label: this.$t('hedisReport.imageQualityOptions.uninterpretable_vitreous_opacity') },
        { value: 'enucleation', label: this.$t('hedisReport.imageQualityOptions.enucleation') },
      ],

      diabetesTypeOptions: [
        { value: 'type_1', label: this.$t('hedisReport.typeI') },
        { value: 'type_2', label: this.$t('hedisReport.typeII') },
        { value: 'unspecified', label: this.$t('hedisReport.unspecified') },
      ],

      referralOptions: [
        { value: 'STAT', label: this.$t('hedisReport.stat') },
        { value: '1 Week', label: this.$t('hedisReport.1Week') },
        { value: '2 Weeks', label: this.$t('hedisReport.2Weeks') },
        { value: '1 Month', label: this.$t('hedisReport.1Month') },
        { value: '6 Months', label: this.$t('hedisReport.6Months') },
      ],

      cptOptions: [
        {
          value: '2026F',
          shortLabelKey: 'hedisReport.cpt_2026F_short',
          fullDescriptionKey: 'hedisReport.cpt_2026F_full'
        },
        {
          value: '2033F',
          shortLabelKey: 'hedisReport.cpt_2033F_short',
          fullDescriptionKey: 'hedisReport.cpt_2033F_full'
        }
      ],
    };
  },
  computed: {
    isRightEyeQualityAdequate() {
      return this.imageQuality.OD === 'Yes';
    },
    isLeftEyeQualityAdequate() {
      return this.imageQuality.OS === 'Yes';
    },
    currentRightEyeConditions() {
      if (this.selectedDiabetesType === 'unspecified') {
        return this.diabetesUnspecifiedConditions.filter(condition => condition.value.endsWith('1'));
      } else if (this.selectedDiabetesType === 'type_1') {
        return this.diabetesType1ConditionsRightEye;
      } else if (this.selectedDiabetesType === 'type_2') {
        return this.diabetesType2ConditionsRightEye;
      }
      return [];
    },
    currentLeftEyeConditions() {
      if (this.selectedDiabetesType === 'unspecified') {
        return this.diabetesUnspecifiedConditions.filter(condition => condition.value.endsWith('2'));
      } else if (this.selectedDiabetesType === 'type_1') {
        return this.diabetesType1ConditionsLeftEye;
      } else if (this.selectedDiabetesType === 'type_2') {
        return this.diabetesType2ConditionsLeftEye;
      }
      return [];
    },
    fullHedisReportData() {
      return {
        imageQuality: { ...this.imageQuality },
        selectedDiabetesType: this.selectedDiabetesType,
        eyeConditions: this.eyeConditions,
        selectedCptCodes: this.selectedCptCode ? [this.selectedCptCode] : [],
        selectedProviderComments: this.selectedProviderComments,
        selectedReferralStatus: this.selectedReferralStatus
      };
    },
  },
  watch: {
    initialData: {
      handler(newVal) {
        if (newVal) {
          this.imageQuality = { ...newVal.imageQuality };
          this.selectedDiabetesType = newVal.selectedDiabetesType;
          this.eyeConditions = { ...newVal.eyeConditions };
          this.selectedCptCode = newVal.selectedCptCodes?.[0] || '';
          this.selectedProviderComments = [...newVal.selectedProviderComments];
          this.selectedReferralStatus = newVal.selectedReferralStatus;

          console.log(this.selectedProviderComments.includes(this.$i18n.t('hedisReport.comment_no_referral').trim()),
                      this.selectedProviderComments.includes(this.$i18n.t('hedisReport.comment_attest').trim()));
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    handleImageQualityChange(eye) {
      if (this.imageQuality[eye] !== 'Yes') {
        this.eyeConditions[eye] = '';
      }
      this.emitFullUpdate();
    },
    handleDiabetesTypeChange() {
      this.eyeConditions = { OD: '', OS: '' };
      this.emitFullUpdate();
    },
    emitFullUpdate() {
      this.$emit('update:hedis-data', this.fullHedisReportData);
    },
    handleArrayUpdate(isChecked, value, targetArray) {
      if (isChecked) {
        if (!targetArray.includes(value)) {
          targetArray.push(value);
        }
      } else {
        const index = targetArray.indexOf(value);
        if (index > -1) {
          targetArray.splice(index, 1);
        }
      }
      this.emitFullUpdate();
    },
    areStringsEqual(string1, string2) {
      if (!string1 || !string2) return false;
      const cleanedString1 = string1.trim().replace(/\s/g, " ");
      const cleanedString2 = string2.trim().replace(/\s/g, " ");
      return cleanedString1 === cleanedString2;
    },
  }
};
</script>
